import React, { useState } from "react";
import {
  Grid,
  Stack,
  Typography,
  Divider,
  Box,
  FormControlLabel,
  Switch,
  Button,
  CircularProgress,
  IconButton,
  Alert,
  MenuItem,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import axios from "axios";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";

import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";
import { updateDocumentHeader } from "../redux/actions/documentHeaderActions";

const validationSchema = Yup.object().shape({
  companySnapshot: Yup.object().shape({
    companyName: Yup.string().required("Company name is required"),
    address: Yup.string().required("Address is required"),
    phone: Yup.string().required("Phone is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    taxId: Yup.string().required("Tax ID is required"),
    logoUrl: Yup.string().url("Invalid URL").nullable(),
  }),
  templateMetadata: Yup.object(),
});

const UpdateDocumentHeaderForm = ({ documentHeader, loading }) => {
  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const meta = documentHeader?.templateMetadata || {};
  const theme = meta.theme || {};
  const labels = meta.labels || {};
  const toggles = meta.toggles || {};
  const footer = meta.footer || {};

  const initialValues = {
    companySnapshot: {
      companyName: documentHeader?.companySnapshot?.companyName || "",
      address: documentHeader?.companySnapshot?.address || "",
      phone: documentHeader?.companySnapshot?.phone || "",
      email: documentHeader?.companySnapshot?.email || "",
      taxId: documentHeader?.companySnapshot?.taxId || "",
      logoUrl: documentHeader?.companySnapshot?.logoUrl || "",
    },
    templateMetadata: {
      theme: {
        primaryColor: theme.primaryColor || "#003366",
        secondaryColor: theme.secondaryColor || "#f8f9fa",
        showLogo: theme.showLogo !== undefined ? theme.showLogo : true,
      },
      labels: {
        documentTitle:
          labels.documentTitle ||
          `${documentHeader?.documentType?.replace("_", " ")}`,
        taxName: labels.taxName || "VAT (16%)",
      },
      toggles: {
        format: toggles.format || "A4", // A4, THERMAL_80MM, or THERMAL_58MM
        showItemDescription:
          toggles.showItemDescription !== undefined
            ? toggles.showItemDescription
            : true,
        showTaxColumn:
          toggles.showTaxColumn !== undefined ? toggles.showTaxColumn : true,
        showBankDetails:
          toggles.showBankDetails !== undefined
            ? toggles.showBankDetails
            : true,
      },
      footer: {
        bankDetails: footer.bankDetails || "",
        termsAndConditions: footer.termsAndConditions || "",
        thankYouMessage:
          footer.thankYouMessage || "Thank you for your business!",
      },
    },
  };

  const handleFileUpload = async (e, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      setUploadError("File is too large. Max size is 1MB.");
      return;
    }

    setUploading(true);
    setUploadError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "GN_Products");

      const { data } = await axios.post(
        "https://api.cloudinary.com/v1_1/dsiyinydr/image/upload",
        formData,
      );

      if (data.secure_url) {
        setFieldValue("companySnapshot.logoUrl", data.secure_url);
      }
    } catch (err) {
      setUploadError("Failed to upload image. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) =>
        dispatch(updateDocumentHeader({ id: documentHeader.id, values }))
      }
      enableReinitialize
    >
      {({ values, dirty, isValid, setFieldValue }) => (
        <Form>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                1. Company Identity & Logo
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="companySnapshot.companyName"
                    label="Company Name"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField name="companySnapshot.taxId" label="Tax ID" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="companySnapshot.email"
                    label="Email"
                    type="email"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField name="companySnapshot.phone" label="Phone" />
                </Grid>
                <Grid item xs={12}>
                  <FormTextField
                    name="companySnapshot.address"
                    label="Address"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    color="text.secondary"
                  >
                    Document Logo
                  </Typography>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: 1,
                        border: "1px solid #ddd",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        bgcolor: "grey.50",
                      }}
                    >
                      {values.companySnapshot.logoUrl ? (
                        <img
                          src={values.companySnapshot.logoUrl}
                          alt="Logo Preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          No Logo
                        </Typography>
                      )}
                    </Box>
                    <Stack spacing={1} flex={1} width="100%">
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          component="label"
                          startIcon={
                            uploading ? (
                              <CircularProgress size={20} color="secondary" />
                            ) : (
                              <CloudUploadIcon />
                            )
                          }
                          disabled={uploading}
                          size="small"
                        >
                          Upload Logo (Max 1MB)
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => handleFileUpload(e, setFieldValue)}
                          />
                        </Button>
                        {values.companySnapshot.logoUrl && (
                          <IconButton
                            color="error"
                            onClick={() =>
                              setFieldValue("companySnapshot.logoUrl", "")
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Stack>
                      <Divider>
                        <Typography variant="caption" color="text.disabled">
                          OR
                        </Typography>
                      </Divider>
                      <FormTextField
                        name="companySnapshot.logoUrl"
                        label="Logo URL"
                        placeholder="https://example.com/logo.png"
                        size="small"
                      />
                    </Stack>
                  </Stack>
                  {uploadError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {uploadError}
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <PaletteOutlinedIcon color="secondary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  2. Visual Theme
                </Typography>
              </Stack>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Primary Color
                  </Typography>
                  <input
                    type="color"
                    value={values.templateMetadata.theme.primaryColor}
                    onChange={(e) =>
                      setFieldValue(
                        "templateMetadata.theme.primaryColor",
                        e.target.value,
                      )
                    }
                    style={{
                      width: "100%",
                      height: "40px",
                      cursor: "pointer",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Secondary Color
                  </Typography>
                  <input
                    type="color"
                    value={values.templateMetadata.theme.secondaryColor}
                    onChange={(e) =>
                      setFieldValue(
                        "templateMetadata.theme.secondaryColor",
                        e.target.value,
                      )
                    }
                    style={{
                      width: "100%",
                      height: "40px",
                      cursor: "pointer",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.templateMetadata.theme.showLogo}
                        onChange={(e) =>
                          setFieldValue(
                            "templateMetadata.theme.showLogo",
                            e.target.checked,
                          )
                        }
                        color="secondary"
                      />
                    }
                    label="Show Logo"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <SettingsOutlinedIcon color="secondary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  3. Document Settings
                </Typography>
              </Stack>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  {/* CORRECTED IMPLEMENTATION using FormTextField */}
                  <FormTextField
                    select
                    name="templateMetadata.toggles.format"
                    label="Print Format"
                    size="small"
                  >
                    <MenuItem value="A4">Standard A4 / Letter</MenuItem>
                    <MenuItem value="THERMAL_80MM">
                      Thermal Receipt (80mm)
                    </MenuItem>
                    <MenuItem value="THERMAL_58MM">
                      Mobile Thermal (58mm)
                    </MenuItem>
                  </FormTextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormTextField
                    name="templateMetadata.labels.documentTitle"
                    label="Document Title"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormTextField
                    name="templateMetadata.labels.taxName"
                    label="Tax Label"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.templateMetadata.toggles.showTaxColumn}
                        onChange={(e) =>
                          setFieldValue(
                            "templateMetadata.toggles.showTaxColumn",
                            e.target.checked,
                          )
                        }
                        color="secondary"
                      />
                    }
                    label="Show Tax Column"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          values.templateMetadata.toggles.showItemDescription
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "templateMetadata.toggles.showItemDescription",
                            e.target.checked,
                          )
                        }
                        color="secondary"
                      />
                    }
                    label="Show Descriptions"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          values.templateMetadata.toggles.showBankDetails
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "templateMetadata.toggles.showBankDetails",
                            e.target.checked,
                          )
                        }
                        color="secondary"
                      />
                    }
                    label="Show Bank Details"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <TextFieldsIcon color="secondary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  4. Footer & Terms
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormTextField
                    name="templateMetadata.footer.thankYouMessage"
                    label="Thank You Message"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="templateMetadata.footer.bankDetails"
                    label="Bank Details"
                    multiline
                    rows={3}
                    placeholder="Bank Name:&#10;Account No:"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="templateMetadata.footer.termsAndConditions"
                    label="Terms & Conditions"
                    multiline
                    rows={3}
                    placeholder="1. Goods once sold..."
                  />
                </Grid>
              </Grid>
            </Box>

            <SubmitButton
              startIcon={<SaveOutlinedIcon />}
              disabled={!dirty || !isValid || loading || uploading}
              loading={loading}
              title="Save Theme Configuration"
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default UpdateDocumentHeaderForm;
